import { cn } from "@/utils/theme";
import { Button, Icon, Input, Switch } from "@elaniintc/goes-design-system";
import { useRef, useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useOnClickOutside } from "usehooks-ts";

interface EditableCellProps {
  index: number;
  rowIndex: number;
  colName: string;
  type: string;
  stripped?: boolean;
  isSelected?: boolean;
  shouldRenderRightBorder?: boolean;
  onAddData: (
    columnId: string,
    value: string | boolean | number,
    registerId: string,
  ) => void;
  canEditRegistry?: boolean;
  canCreateRegistry: boolean;
}

const EditableCell = ({
  type,
  colName,
  index,
  rowIndex,
  stripped,
  isSelected = false,
  shouldRenderRightBorder = true,
  canEditRegistry,
  canCreateRegistry,
  onAddData,
}: EditableCellProps) => {
  const { watch } = useFormContext();
  const [currentValue, id] = watch([
    `rows.${rowIndex}.${colName}`,
    `rows.${rowIndex}.id`,
  ]);

  const isUnsavedAvailableToEdit =
    canCreateRegistry && currentValue !== null && !id;

  const [state, setState] = useState<"idle" | "edit">(
    currentValue === "" ? "edit" : "idle",
  );

  if (type === "BOOLEAN") {
    return (
      <td
        className={cn(
          "border-b border-neutral p-3 text-sm border-x",
          isSelected
            ? "bg-surface-action-accent-quiet"
            : stripped
              ? "bg-neutral-object-accent-silent"
              : "bg-neutral-object",
          !shouldRenderRightBorder && "border-r-0",
        )}
      >
        <Switch
          id={`switch-${rowIndex}-${colName}`}
          checked={currentValue}
          onCheckedChange={(checked) => {
            if (!canEditRegistry) return;
            onAddData(colName, checked ? 1 : 0, id);
          }}
        />
      </td>
    );
  }

  if (state === "idle") {
    return (
      <td
        className={cn(
          "border-b border-neutral p-3 text-sm border-x max-w-50 truncate text-body-loud",
          isSelected
            ? "bg-surface-action-accent-quiet"
            : stripped
              ? "bg-neutral-object-accent-silent"
              : "bg-neutral-object",
          shouldRenderRightBorder && "border-r-0",
        )}
        onClick={() => {
          if (canEditRegistry || isUnsavedAvailableToEdit) {
            setState("edit");
          }
        }}
      >
        {currentValue && <span className="text-body-loud">{currentValue}</span>}
        {currentValue === null && (
          <span className="text-on-disabled font-normal">
            {type === "STRING" ? "Escribe un texto" : "Escribe un número"}
          </span>
        )}
      </td>
    );
  }
  if (state === "edit") {
    if (type === "STRING") {
      return (
        <EditableTextField
          setState={setState}
          value={currentValue}
          colName={colName}
          index={index}
          rowIndex={rowIndex}
          onAddData={(columnId, value) => {
            onAddData(columnId, value, id);
          }}
        />
      );
    }
    if (type === "INTEGER") {
      return (
        <EditableTextField
          setState={setState}
          value={currentValue?.toString()}
          isNumberInput
          colName={colName}
          index={index}
          placeholder="Escribe un número..."
          rowIndex={rowIndex}
          onAddData={(columnId, value) => {
            onAddData(columnId, parseInt(value, 10), id);
          }}
        />
      );
    }

    if (type === "FLOAT") {
      return (
        <EditableTextField
          setState={setState}
          value={currentValue?.toString()}
          isNumberInput
          isDecimalInput
          colName={colName}
          index={index}
          placeholder="Escribe un número..."
          rowIndex={rowIndex}
          onAddData={(columnId, value) => {
            onAddData(columnId, parseFloat(value), id);
          }}
        />
      );
    }
  }
};

export default EditableCell;

interface EditableTextFieldProps {
  index: number;
  rowIndex?: number;
  colName?: string;
  value?: string;
  isNumberInput?: boolean;
  isDecimalInput?: boolean;
  placeholder?: string;
  setState: (state: "idle" | "edit") => void;
  onAddData: (columnId: string, value: string) => void;
}

const EditableTextField = ({
  setState,
  value,
  colName,
  rowIndex,
  isNumberInput,
  isDecimalInput,
  placeholder,
  onAddData,
}: EditableTextFieldProps) => {
  const [tempValue, setTempValue] = useState(value || "");
  const { setValue } = useFormContext();

  const [containerWidth, setContainerWidth] = useState(0);
  const ref = useRef<HTMLTableCellElement>(
    null,
  ) as React.RefObject<HTMLElement>;

  useEffect(() => {
    if (!ref.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0]) {
        setContainerWidth(entries[0].contentRect.width);
      }
    });

    resizeObserver.observe(ref.current);

    return () => resizeObserver.disconnect();
  }, []);

  const onSubmit = () => {
    onAddData(colName || "", tempValue);
    setValue(`rows.${rowIndex}.${colName}`, tempValue);
  };

  useOnClickOutside(ref, () => {
    setState("idle");
    setTempValue(value || ""); // Reset to original value on outside click
  });

  return (
    <td
      ref={ref as React.RefObject<HTMLTableCellElement>}
      className={cn(
        "border-b border-neutral bg-neutral-object border-l p-3 text-sm relative",
      )}
    >
      <div className="invisible">{value || "Escribe un texto"}</div>
      <div
        className={cn(
          "absolute bg-neutral-object top-1.5 z-50",
          containerWidth < 180 ? "right-4" : "left-3 right-0",
        )}
      >
        <Input
          type="text"
          variant="xs"
          placeholder={placeholder || "Escribe un texto..."}
          value={tempValue}
          maxLength={isNumberInput ? 10 : 250}
          onChange={(e) => {
            if (isDecimalInput) {
              // Use regex to match valid number format with max 4 decimal places
              const match = e.target.value.match(/^\d*\.?\d{0,4}/);
              const value = match ? match[0] : "";
              setTempValue(value);
            } else if (isNumberInput) {
              const value = e.target.value;
              if (!value || /^\d+$/.test(value)) {
                setTempValue(value);
              }
            } else {
              setTempValue(e.target.value);
            }
          }}
          className="max-w-[320px] shadow-action-md"
        />
        <div
          className={cn(
            "absolute translate-y-0.5 bg-neutral-object shadow-action-md rounded-lg",
          )}
        >
          <Button
            group="left"
            size="icon"
            variant="tertiary"
            aria-label="Cancelar edición"
            onClick={() => setState("idle")}
          >
            <Icon type="far" name="times" />
          </Button>
          <Button
            group="right"
            size="icon"
            variant="tertiary"
            disabled={!tempValue || tempValue.trim().length === 0}
            aria-label="Guardar cambios"
            onClick={() => {
              onSubmit();
              setState("idle");
            }}
          >
            <Icon type="far" name="check" />
          </Button>
        </div>
      </div>
    </td>
  );
};
